import React, { useState } from 'react';

import { send } from 'loot-core/src/platform/client/fetch';

import { theme } from '../../style';
import { Button } from '../common/Button2';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '../common/Modal';
import { Text } from '../common/Text';
import { View } from '../common/View';

import {
  type BillingSummary,
  accessEnds,
  errorText,
  formatDate,
  formatMoney,
  isInApp,
  openStoreSubscriptions,
} from './billing';

type Step = 'reason' | 'offer' | 'confirm' | 'store' | 'done' | 'switched';

type CancelSubscriptionModalProps = {
  summary: BillingSummary;
  onDone?: () => void;
};

// The "Why are you leaving us?" survey. A reason is required before anyone
// can cancel; "Other" also needs a few words.
export function CancelSubscriptionModal({
  summary,
  onDone,
}: CancelSubscriptionModalProps) {
  const isStripe = summary.source === 'stripe' && summary.isCurrent;
  const canOfferBasic = isStripe && summary.plan === 'premium';

  const [step, setStep] = useState<Step>('reason');
  const [reason, setReason] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [offeredBasic, setOfferedBasic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsDetails = reason === 'other';
  const reasonComplete =
    reason !== null && (!needsDetails || details.trim().length > 0);

  const url = String(window.location.href);

  function next() {
    if (!reasonComplete) return;
    setError(null);
    if (!isStripe) {
      setStep('store');
    } else if (canOfferBasic) {
      setOfferedBasic(true);
      setStep('offer');
    } else {
      setStep('confirm');
    }
  }

  async function cancel() {
    setBusy(true);
    setError(null);
    const result = await send('billing-cancel', {
      url,
      reason,
      details: details.trim(),
      offeredBasic,
      inApp: isInApp(),
    });
    setBusy(false);
    if (result.error) {
      setError(errorText(result.error));
      return;
    }
    if (result.data?.handoff === 'store') {
      openStoreSubscriptions();
      onDone?.();
      return 'close';
    }
    setStep('done');
    onDone?.();
  }

  async function switchToBasic() {
    setBusy(true);
    setError(null);
    const result = await send('billing-switch-plan', {
      url,
      plan: 'basic',
      fromCancel: true,
      inApp: isInApp(),
    });
    setBusy(false);
    if (result.error) {
      setError(errorText(result.error));
      return;
    }
    setStep('switched');
    onDone?.();
  }

  const titles: Record<Step, string> = {
    reason: 'Why are you leaving us?',
    offer: 'Before you go',
    confirm: 'Cancel your subscription?',
    store: 'Cancel in the App Store',
    done: 'Your subscription is cancelled',
    switched: "You're now on Basic",
  };

  return (
    <Modal
      name="cancel-subscription"
      containerProps={{ style: { width: 520, maxWidth: '100%' } }}
    >
      {({ state: { close } }) => (
        <>
          <ModalHeader
            title={<ModalTitle title={titles[step]} shrinkOnOverflow />}
            rightContent={<ModalCloseButton onPress={close} />}
          />

          {step === 'reason' && (
            <View style={{ gap: 8 }}>
              <Text style={{ color: theme.pageTextSubdued, marginBottom: 6 }}>
                Your answer goes straight to the MyBudgetCoach team and helps
                us make the app better.
              </Text>
              <View role="radiogroup" aria-label="Reason for leaving" style={{ gap: 6 }}>
                {summary.cancelReasons.map(option => {
                  const selected = reason === option.id;
                  return (
                    <label
                      key={option.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        border: `1px solid ${selected ? theme.buttonPrimaryBackground : theme.tableBorder}`,
                        backgroundColor: selected
                          ? theme.tableRowBackgroundHover
                          : theme.tableBackground,
                        color: theme.pageText,
                      }}
                    >
                      <input
                        type="radio"
                        name="cancel-reason"
                        value={option.id}
                        checked={selected}
                        onChange={() => setReason(option.id)}
                        style={{ margin: 0, accentColor: theme.buttonPrimaryBackground }}
                      />
                      {option.label}
                    </label>
                  );
                })}
              </View>

              {needsDetails && (
                <View style={{ marginTop: 6, gap: 4 }}>
                  <label
                    htmlFor="cancel-details"
                    style={{ color: theme.pageText, fontWeight: 500 }}
                  >
                    Please tell us more
                  </label>
                  <textarea
                    id="cancel-details"
                    autoFocus
                    rows={3}
                    maxLength={1000}
                    value={details}
                    onChange={e => setDetails(e.target.value)}
                    style={{
                      resize: 'vertical',
                      padding: 8,
                      borderRadius: 6,
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      border: `1px solid ${theme.formInputBorder}`,
                      backgroundColor: theme.formInputBackground,
                      color: theme.formInputText,
                    }}
                  />
                </View>
              )}

              <ModalButtons>
                <Button onPress={close} style={{ marginRight: 10 }}>
                  Keep my subscription
                </Button>
                <Button variant="primary" isDisabled={!reasonComplete} onPress={next}>
                  Continue
                </Button>
              </ModalButtons>
            </View>
          )}

          {step === 'offer' && (
            <View style={{ gap: 10, lineHeight: 1.5 }}>
              <Text>
                Would Basic suit you better? It's{' '}
                <strong>{formatMoney(summary.prices.basic, summary.currency)} a month</strong>{' '}
                instead of {formatMoney(summary.prices.premium, summary.currency)},
                and you keep your budget, bank connections and messages with
                your coach. The monthly session with your coach isn't included.
              </Text>
              <Text style={{ color: theme.pageTextSubdued }}>
                The change happens now, and your next bill will be at the Basic
                price.
              </Text>
              {error && <Text style={{ color: theme.errorText }}>{error}</Text>}
              <ModalButtons>
                <Button
                  isDisabled={busy}
                  onPress={() => setStep('confirm')}
                  style={{ marginRight: 10 }}
                >
                  No thanks, cancel
                </Button>
                <Button variant="primary" isDisabled={busy} onPress={switchToBasic}>
                  Switch to Basic
                </Button>
              </ModalButtons>
            </View>
          )}

          {step === 'confirm' && (
            <View style={{ gap: 10, lineHeight: 1.5 }}>
              <Text>
                {summary.status === 'trialing'
                  ? `Your free trial ends on ${formatDate(accessEnds(summary))}, and you won't be charged.`
                  : `You'll keep full access until ${formatDate(accessEnds(summary))}, and you won't be charged again.`}
              </Text>
              <Text style={{ color: theme.pageTextSubdued }}>
                You can undo this from the Billing page any time before then.
              </Text>
              {error && <Text style={{ color: theme.errorText }}>{error}</Text>}
              <ModalButtons>
                <Button isDisabled={busy} onPress={close} style={{ marginRight: 10 }}>
                  Keep my subscription
                </Button>
                <Button
                  variant="primary"
                  isDisabled={busy}
                  onPress={cancel}
                  style={{ backgroundColor: theme.errorText, borderColor: theme.errorText }}
                >
                  {busy ? 'Cancelling…' : 'Cancel subscription'}
                </Button>
              </ModalButtons>
            </View>
          )}

          {step === 'store' && (
            <View style={{ gap: 10, lineHeight: 1.5 }}>
              <Text>
                Thanks for telling us. Your subscription is through Apple, so
                it's cancelled in the App Store rather than here.
              </Text>
              <Text style={{ color: theme.pageTextSubdued }}>
                {isInApp()
                  ? "Tap Continue and we'll open Apple's subscription screen."
                  : 'On your iPhone, open Settings, tap your name, then Subscriptions, and choose MyBudgetCoach.'}
              </Text>
              {error && <Text style={{ color: theme.errorText }}>{error}</Text>}
              <ModalButtons>
                <Button isDisabled={busy} onPress={close} style={{ marginRight: 10 }}>
                  Keep my subscription
                </Button>
                <Button
                  variant="primary"
                  isDisabled={busy}
                  onPress={async () => {
                    if ((await cancel()) === 'close') close();
                  }}
                >
                  Continue
                </Button>
              </ModalButtons>
            </View>
          )}

          {step === 'done' && (
            <View style={{ gap: 10, lineHeight: 1.5 }}>
              <Text>
                {summary.status === 'trialing'
                  ? `Your free trial ends on ${formatDate(accessEnds(summary))}. You won't be charged.`
                  : `You'll keep full access until ${formatDate(accessEnds(summary))}. You won't be charged again.`}
              </Text>
              <Text style={{ color: theme.pageTextSubdued }}>
                Changed your mind? Choose "Keep my subscription" on the Billing
                page before then.
              </Text>
              <ModalButtons>
                <Button variant="primary" onPress={close}>
                  Close
                </Button>
              </ModalButtons>
            </View>
          )}

          {step === 'switched' && (
            <View style={{ gap: 10, lineHeight: 1.5 }}>
              <Text>
                Thanks for staying. Your next bill will be{' '}
                {formatMoney(summary.prices.basic, summary.currency)}.
              </Text>
              <ModalButtons>
                <Button variant="primary" onPress={close}>
                  Close
                </Button>
              </ModalButtons>
            </View>
          )}
        </>
      )}
    </Modal>
  );
}
